import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Plus, Edit2, Trash2, Search, Users, Eye, EyeOff, AlertTriangle, Settings } from 'lucide-react';
import AddressFields from '../../../components/AddressFields';
import '../../../styles/admin/ERPClients.css';


const ERPClients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('actifs');
  const [filterVille, setFilterVille] = useState('tous');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    date: true,
    numero: true,
    nom: true,
    prenom: true,
    email: true,
    telephone: true,
    adresse: true,
    ville: true,
    superficie: true,
    statut: true,
    actions: true
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    code_postal: '',
    superficie: '',
    type_client: 'residentiel',
    notes: '',
    bypassEmail: false
  });

  useEffect(() => {
    loadUserPreferences();
    fetchClients();
  }, []);

  useEffect(() => {
    // Close column menu when clicking outside
    const handleClickOutside = (e) => {
      if (showColumnMenu && !e.target.closest('.column-menu') && !e.target.closest('.btn-secondary')) {
        setShowColumnMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnMenu]);

  // Load user preferences
  const loadUserPreferences = async () => {
    try {
      const response = await api.get('/api/admin/user/preferences', {
      });
      
      if (response.data.column_preferences) {
        setVisibleColumns({
          ...response.data.column_preferences,
          actions: true // Always show actions column
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoadingPreferences(false);
    }
  };

  // Save user preferences (with debounce)
  const saveUserPreferences = async (newPreferences) => {
    try {
      await api.put(
        '/api/admin/user/preferences',
        { column_preferences: newPreferences },
      );
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/api/admin/submissions', {
      });
      // API returns {submissions: [...], total: N}
      const clientsData = response.data.submissions || response.data.data || response.data || [];
      setClients(clientsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clients:', error);
      if (error.response?.status === 401) {
        
        navigate('/auth/login');
      }
      setLoading(false);
    }
  };

  const extractCity = (address) => {
    if (!address) return '';
    const parts = address.split(',');
    return parts.length >= 2 ? parts[parts.length - 2].trim() : '';
  };

  const getUniqueCities = () => {
    const cities = clients
      .map(c => extractCity(c.adresse))
      .filter(city => city && city.trim() !== '');
    return [...new Set(cities)].sort();
  };

  const getFilteredAndSortedClients = () => {
    let filtered = clients.filter(client => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        client.prenom?.toLowerCase().includes(searchLower) ||
        client.nom?.toLowerCase().includes(searchLower) ||
        client.courriel?.toLowerCase().includes(searchLower) ||
        client.telephone?.includes(searchTerm) ||
        client.numero_client?.includes(searchTerm);
      
      const matchesStatut = filterStatut === 'tous' || 
        (filterStatut === 'actifs' && client.actif !== false) ||
        (filterStatut === 'inactifs' && client.actif === false);
      
      const clientCity = extractCity(client.adresse);
      const matchesVille = filterVille === 'tous' || clientCity === filterVille;
      
      return matchesSearch && matchesStatut && matchesVille;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'date':
          aVal = new Date(a.date || a.timestamp);
          bVal = new Date(b.date || b.timestamp);
          break;
        case 'numero':
          aVal = a.numero_client || '';
          bVal = b.numero_client || '';
          break;
        case 'nom':
          aVal = `${a.nom} ${a.prenom}`.toLowerCase();
          bVal = `${b.nom} ${b.prenom}`.toLowerCase();
          break;
        case 'ville':
          aVal = extractCity(a.adresse);
          bVal = extractCity(b.adresse);
          break;
        case 'superficie':
          aVal = parseInt(a.propertyArea) || 0;
          bVal = parseInt(b.propertyArea) || 0;
          break;
        default:
          aVal = a[sortField];
          bVal = b[sortField];
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredClients = getFilteredAndSortedClients();
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation stricte
    const errors = [];
    
    if (!formData.prenom.trim()) errors.push('Prénom');
    if (!formData.nom.trim()) errors.push('Nom');
    if (!formData.bypassEmail && !formData.email.trim()) errors.push('Courriel');
    if (!formData.adresse.trim()) errors.push('Adresse complète');
    if (!formData.ville.trim()) errors.push('Ville');
    if (!formData.code_postal.trim()) errors.push('Code postal');
    if (!formData.telephone.trim()) errors.push('Téléphone');
    if (!formData.superficie || parseInt(formData.superficie) <= 0) errors.push('Superficie (doit être > 0)');
    
    if (errors.length > 0) {
      alert(`⚠️ Champs obligatoires manquants:\n\n${errors.map(e => `• ${e}`).join('\n')}\n\nVeuillez remplir tous les champs requis.`);
      return;
    }
    
    try {
      // Reconstruct full address with city and postal code
      const adresseComplete = `${formData.adresse}, ${formData.ville}, QC ${formData.code_postal}`;
      
      const clientData = {
        prenom: formData.prenom.trim(),
        nom: formData.nom.trim(),
        adresse: adresseComplete,
        courriel: formData.bypassEmail ? 'noemail@client.local' : formData.email.trim(),
        telephone: formData.telephone.trim(),
        sourceReference: 'Création manuelle',
        services: ['Autre'],
        notes: formData.notes.trim(),
        propertyArea: parseInt(formData.superficie),
        propertyCoordinates: []
      };

      if (editingClient) {
        await api.put(
          `/api/admin/submissions/${editingClient.id}`,
          clientData,
        );
      } else {
        await api.post(
          '/api/admin/clients',
          clientData,
        );
      }

      setShowModal(false);
      setFormData({
        prenom: '',
        nom: '',
        email: '',
        telephone: '',
        adresse: '',
        ville: '',
        code_postal: '',
        superficie: '',
        type_client: 'residentiel',
        notes: '',
        bypassEmail: false
      });
      fetchClients();
    } catch (error) {
      alert('Erreur lors de la sauvegarde du client');
    }
  };

  const handleToggleActive = async (client) => {
    const action = client.actif !== false ? 'désactiver' : 'activer';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ce client?`)) return;

    try {
      await api.patch(
        `/api/admin/clients/${client.id}/toggle-active`,
        {},
      );
      fetchClients();
    } catch (error) {
      alert(`Erreur lors de la modification du statut`);
    }
  };

  const handleDeletePermanent = async (client) => {
    try {
      // Check dependencies first
      const depsResponse = await api.get(
        `/api/admin/clients/${client.id}/dependencies`,
      );

      const { has_dependencies, quotes_count, invoices_count } = depsResponse.data;

      if (has_dependencies) {
        const message = `⚠️ ATTENTION: Ce client a:\n- ${quotes_count} devis\n- ${invoices_count} factures\n\nImpossible de supprimer définitivement.\nUtilisez "Désactiver" à la place.`;
        alert(message);
        return;
      }

      const confirmMsg = `🗑️ SUPPRIMER DÉFINITIVEMENT\n\n${client.prenom} ${client.nom}\n\nCette action est IRRÉVERSIBLE.\nConfirmer?`;
      if (!window.confirm(confirmMsg)) return;

      await api.delete(
        `/api/admin/clients/${client.id}/permanent`,
      );

      alert('✅ Client supprimé définitivement');
      fetchClients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="erp-clients-page">
      <div className="erp-clients-header">
        <div className="erp-clients-header-content">
          <Link to="/admin/erp" className="erp-back-link">
            <ArrowLeft size={20} />
            Retour au dashboard
          </Link>
          <div className="header-title-section">
            <h1><Users size={32} /> Clients</h1>
            <p>{filteredClients.length} client{filteredClients.length > 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      <div className="erp-clients-content">
        <div className="clients-toolbar">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Rechercher (nom, email, téléphone, ID)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filters-row">
            <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)} className="filter-select">
              <option value="tous">Tous</option>
              <option value="actifs">Actifs seulement</option>
              <option value="inactifs">Inactifs seulement</option>
            </select>
            
            <select value={filterVille} onChange={(e) => setFilterVille(e.target.value)} className="filter-select">
              <option value="tous">Toutes les villes</option>
              {getUniqueCities().map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
          
          <div style={{display: 'flex', gap: '0.75rem', position: 'relative'}}>
            <button 
              className="btn-secondary" 
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              style={{display: 'inline-flex', alignItems: 'center', gap: '0.5rem'}}
            >
              <Settings size={18} />
              Colonnes
            </button>
            
            {showColumnMenu && (
              <div className="column-menu">
                <div className="column-menu-header">
                  <strong>Afficher les colonnes</strong>
                </div>
                {Object.keys(visibleColumns).map(col => (
                  col !== 'actions' && (
                    <label key={col} className="column-menu-item">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns[col]}
                        onChange={(e) => {
                          const newPreferences = {...visibleColumns, [col]: e.target.checked};
                          setVisibleColumns(newPreferences);
                          // Save to backend (without actions column)
                          const { actions, ...prefsToSave } = newPreferences;
                          saveUserPreferences(prefsToSave);
                        }}
                      />
                      <span>
                        {col === 'date' && 'Date de création'}
                        {col === 'numero' && 'N° Client'}
                        {col === 'nom' && 'Nom'}
                        {col === 'prenom' && 'Prénom'}
                        {col === 'email' && 'Courriel'}
                        {col === 'telephone' && 'Téléphone'}
                        {col === 'adresse' && 'Adresse'}
                        {col === 'ville' && 'Ville'}
                        {col === 'superficie' && 'Superficie (pi²)'}
                        {col === 'statut' && 'Statut'}
                      </span>
                    </label>
                  )
                ))}
              </div>
            )}
            
            <button className="btn-primary" onClick={() => { 
              setEditingClient(null); 
              setFormData({
                prenom:'',
                nom:'',
                email:'',
                telephone:'',
                adresse:'',
                ville:'',
                code_postal:'',
                superficie:'',
                type_client:'residentiel',
                notes:'',
                bypassEmail:false
              }); 
              setShowModal(true); 
            }}>
              <Plus size={20} />
              Nouveau client
            </button>
          </div>
        </div>

        <div className="clients-table-container">
          {filteredClients.length === 0 ? (
            <div className="empty-state">
              <Users size={64} color="#ccc" />
              <p>Aucun client trouvé</p>
            </div>
          ) : (
            <>
              <table className="clients-table">
                <thead>
                  <tr>
                    {visibleColumns.date && (
                      <th onClick={() => handleSort('date')} className="sortable">
                        Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    )}
                    {visibleColumns.numero && (
                      <th onClick={() => handleSort('numero')} className="sortable">
                        ID {sortField === 'numero' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    )}
                    {visibleColumns.nom && (
                      <th onClick={() => handleSort('nom')} className="sortable">
                        Nom {sortField === 'nom' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    )}
                    {visibleColumns.prenom && <th>Prénom</th>}
                    {visibleColumns.email && <th>Courriel</th>}
                    {visibleColumns.telephone && <th>Téléphone</th>}
                    {visibleColumns.adresse && <th>Adresse</th>}
                    {visibleColumns.ville && (
                      <th onClick={() => handleSort('ville')} className="sortable">
                        Ville {sortField === 'ville' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    )}
                    {visibleColumns.superficie && (
                      <th onClick={() => handleSort('superficie')} className="sortable">
                        Superficie (pi²) {sortField === 'superficie' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    )}
                    {visibleColumns.statut && <th>Statut</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClients.map(client => (
                    <tr key={client.id} onClick={() => navigate(`/admin/erp/clients/${client.id}`)} className="client-row">
                      {visibleColumns.date && <td>{new Date(client.date || client.timestamp).toLocaleDateString('fr-CA')}</td>}
                      {visibleColumns.numero && <td><strong>{client.numero_client || 'N/A'}</strong></td>}
                      {visibleColumns.nom && <td>{client.nom}</td>}
                      {visibleColumns.prenom && <td>{client.prenom}</td>}
                      {visibleColumns.email && <td>{client.courriel}</td>}
                      {visibleColumns.telephone && <td>{client.telephone || 'N/A'}</td>}
                      {visibleColumns.adresse && <td className="address-cell">{client.adresse}</td>}
                      {visibleColumns.ville && <td>{extractCity(client.adresse) || 'N/A'}</td>}
                      {visibleColumns.superficie && <td className="text-right">{client.propertyArea ? client.propertyArea.toLocaleString() : 'N/A'}</td>}
                      {visibleColumns.statut && (
                        <td>
                          <span className={`status-badge ${client.actif !== false ? 'active' : 'inactive'}`}>
                            {client.actif !== false ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                      )}
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon-small" 
                            onClick={() => { 
                              setEditingClient(client); 
                              const hasEmail = client.courriel && client.courriel !== 'noemail@client.local';
                              
                              // Parse address into components
                              let adresse = '', ville = '', code_postal = '';
                              if (client.adresse) {
                                const parts = client.adresse.split(',').map(p => p.trim());
                                if (parts.length >= 1) {
                                  adresse = parts[0]; // Street number + street name
                                }
                                if (parts.length >= 2) {
                                  ville = parts[1];
                                }
                                if (parts.length >= 3) {
                                  // Extract postal code from "QC J1H 5H3"
                                  const provincePostal = parts[2].split(' ');
                                  code_postal = provincePostal.slice(1).join(' ') || '';
                                }
                              }
                              
                              setFormData({
                                prenom: client.prenom || '',
                                nom: client.nom || '',
                                email: hasEmail ? client.courriel : '',
                                telephone: client.telephone || '',
                                adresse: adresse,
                                ville: ville,
                                code_postal: code_postal,
                                superficie: client.propertyArea || '',
                                type_client: 'residentiel',
                                notes: client.notes || '',
                                bypassEmail: !hasEmail
                              }); 
                              setShowModal(true); 
                            }} 
                            title="Modifier">
                            <Edit2 size={16} />
                          </button>
                          <button className="btn-icon-small toggle" onClick={() => handleToggleActive(client)} title={client.actif !== false ? 'Désactiver' : 'Activer'}>
                            {client.actif !== false ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button className="btn-icon-small delete" onClick={() => handleDeletePermanent(client)} title="Supprimer définitivement">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="pagination">
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="btn-pagination">Précédent</button>
                  <div className="page-numbers">
                    {[...Array(totalPages)].map((_, i) => (
                      <button key={i + 1} onClick={() => handlePageChange(i + 1)} className={`btn-page ${currentPage === i + 1 ? 'active' : ''}`}>{i + 1}</button>
                    ))}
                  </div>
                  <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="btn-pagination">Suivant</button>
                  <span className="pagination-info">Page {currentPage} sur {totalPages}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingClient ? 'Modifier' : 'Nouveau'} client</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom *</label>
                  <input type="text" required value={formData.prenom} onChange={(e) => setFormData({...formData, prenom: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input type="text" required value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Courriel {!formData.bypassEmail && '*'}</label>
                  <input 
                    type="email" 
                    required={!formData.bypassEmail}
                    disabled={formData.bypassEmail}
                    value={formData.email} 
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder={formData.bypassEmail ? 'Email non requis' : ''}
                  />
                  <label style={{marginTop: '0.5rem', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666'}}>
                    <input 
                      type="checkbox" 
                      checked={formData.bypassEmail}
                      onChange={(e) => setFormData({...formData, bypassEmail: e.target.checked, email: e.target.checked ? '' : formData.email})}
                    />
                    Client sans courriel
                  </label>
                </div>
                <div className="form-group">
                  <label>Téléphone *</label>
                  <input type="tel" required value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})} />
                </div>
              </div>
              <div className="form-group full-width">
                <AddressFields
                  adresse={formData.adresse}
                  ville={formData.ville}
                  codePostal={formData.code_postal}
                  onChange={(addressData) => {
                    // IMPORTANT: Use functional update to preserve other fields
                    setFormData(prev => ({
                      ...prev,
                      adresse: addressData.adresse,
                      ville: addressData.ville,
                      code_postal: addressData.code_postal
                    }));
                  }}
                />
              </div>
              <div className="form-group">
                <label>Superficie (pi²) *</label>
                <input type="number" required min="1" value={formData.superficie} onChange={(e) => setFormData({...formData, superficie: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea rows="3" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn-primary">{editingClient ? 'Mettre à jour' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPClients;
